import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ADMIN_PAGES_KEYS, NS } from '../../i18n/constants/keys';
import { createSkill, createSubSkill } from '../../utils/api_service/skill_api';
import {
  convertFileToBase64,
  validateQuestionMedia,
} from '../../utils/helpers/fileHandler';
import { addNewSkill } from '../../utils/redux_store/features/skill_reducer';
import { addNewSubSkill } from '../../utils/redux_store/features/subskill_reducer';
import { Question, Skill, SubSkill } from '../../utils/types/assessment_types';
import { SelectOptions } from '../../utils/types/react_select_types';
import { UploadMedia } from '../collapsible_question/upload_media/upload_media';
import { Button } from '../components';
import { CustomSelect } from '../custom_react_select/custom_select';
import { MinuteSecondsInput } from '../input/minute_seconds_input/minute_seconds_input';
import { TextArea } from '../input/text_area/text_area';
import { TextInput } from '../input/text_input/text_input';

type AddQuestionModelProps = {
  addNewQuestion: (question: Question) => void;
  skillList: SelectOptions[];
  levelList: SelectOptions[];
  jobTitleList: SelectOptions[];
  subSkillList: SubSkill[] | undefined;
  setSkillList: any;
};

export const AddQuestionModal = ({
  addNewQuestion,
  skillList,
  levelList,
  jobTitleList,
  subSkillList,
  setSkillList,
}: AddQuestionModelProps) => {
  const newQuestionState: Question = {
    id: '',
    jobId: '',
    question: '',
    answer: '',
    skillId: '',
    levelId: '',
    subSkill1Id: '',
    subSkill2Id: '',
    subSkill3Id: '',
    questionType: 'Modern',
    questionTheme: '',
    time: 30,
    points: 1,
    fileEncoded: null,
    mediaQuestion: false,
    mediaExtension: null,
    jobTitle: [],
  };
  const { t } = useTranslation(NS.adminPages);
  const AQM = ADMIN_PAGES_KEYS.jobOpenings.addQuestionModal;
  const dispatch = useDispatch();
  const [newQuestion, setNewQuestion] = useState<Question>(newQuestionState);
  const [subSkills1, setSubSkills1] = useState<SelectOptions[]>([]);
  const [subSkills2, setSubSkills2] = useState<SelectOptions[]>([]);
  const [subSkills3, setSubSkills3] = useState<SelectOptions[]>([]);

  const returnSubSkills = (parent: string | undefined) => {
    let subSkills: SelectOptions[] = [];
    subSkillList?.map((subSkill: SubSkill) => {
      if (subSkill.parentSkillId === parent) {
        subSkills.push({ label: subSkill.subskillName, value: subSkill.id });
      }
    });

    return subSkills;
  };

  const questionTypes: SelectOptions[] = [
    {
      value: 'Modern',
      label: 'Video',
    },
    {
      value: 'Classic',
      label: 'Text',
    },
  ];

  const updateSubSkillList = (
    level: string,
    subSkillId: string,
    skillName: string,
  ) => {
    if (level === '1') {
      setSubSkills1((subSkills) => [
        ...subSkills,
        { value: subSkillId, label: skillName },
      ]);
    } else if (level === '2') {
      setSubSkills2((subSkills) => [
        ...subSkills,
        { value: subSkillId, label: skillName },
      ]);
    } else if (level === '3') {
      setSubSkills3((subSkills) => [
        ...subSkills,
        { value: subSkillId, label: skillName },
      ]);
    }
  };

  const onAddNewSkill = (
    skill: string,
    parent?: string | null,
    level?: string,
  ) => {
    if (parent) {
      createSubSkill(skill, parent)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
        })
        .then((response: SubSkill) => {
          if (level) {
            updateSubSkillList(level, response.id, skill);
            dispatch(addNewSubSkill(response));
          }
        });
    } else {
      createSkill(skill)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
        })
        .then((response: Skill) => {
          setSkillList((skills: any) => [
            ...skills,
            { value: response.id, label: skill },
          ]);
          dispatch(addNewSkill({ value: response.id, label: skill }));
        });
    }
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | any>,
  ) => {
    if (event.target.name === 'time') {
      setNewQuestion((prev) => ({
        ...prev,
        time: event.target.valueAsNumber,
      }));
    } else {
      setNewQuestion((prev) => ({
        ...prev,
        [event.target.name]: event.target.value,
      }));
    }
  };

  const handleMultiSelectChange = (
    selectedValue: SelectOptions[] | null,
    action: any,
  ) => {
    setNewQuestion((prev) => ({
      ...prev,
      [action.name]: selectedValue?.map((jobTitle) => jobTitle.value) ?? [],
    }));
  };

  const handleSelectChange = (selectedValue: SelectOptions, action: any) => {
    if (action.name === 'skillId') {
      selectedValue &&
        setSubSkills1(() => returnSubSkills(selectedValue.value));
      setSubSkills2([]);
      setSubSkills3([]);
      setNewQuestion((question) => ({
        ...question,
        [action.name]: selectedValue ? selectedValue.value : null,
        subSkill1Id: null,
        subSkill2Id: null,
        subSkill3Id: null,
      }));
    } else if (action.name === 'subSkill1Id') {
      selectedValue &&
        setSubSkills2(() => returnSubSkills(selectedValue.value));
      setSubSkills3([]);
      setNewQuestion((question) => ({
        ...question,
        [action.name]: selectedValue ? selectedValue.value : null,
        subSkill2Id: null,
        subSkill3Id: null,
      }));
    } else if (action.name === 'subSkill2Id') {
      selectedValue &&
        setSubSkills3(() => returnSubSkills(selectedValue.value));
      setNewQuestion((question) => ({
        ...question,
        [action.name]: selectedValue ? selectedValue.value : null,
        subSkill3Id: null,
      }));
    } else {
      setNewQuestion((prev) => ({
        ...prev,
        [action.name]: selectedValue ? selectedValue.value : null,
      }));
    }
  };

  const addFileViaInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files?.item(0);
    if (file && validateQuestionMedia(file)) {
      convertFileToBase64(file).then((result) => {
        setNewQuestion((prev) => ({
          ...prev,
          mediaQuestion: true,
          mediaExtension: file.name.split('.').pop(),
          // @ts-ignore
          fileEncoded: result as string,
        }));
      });
    }
  };

  const clearMediaFile = () => {
    setNewQuestion((prev) => ({
      ...prev,
      mediaQuestion: false,
      mediaExtension: '',
      fileEncoded: '',
    }));
  };

  const selectedJobTitles = jobTitleList.filter((option) =>
    newQuestion.jobTitle?.includes(option.value),
  );

  return (
    <div className='space-y-4'>
      <div className='rounded-lg border border-slate-100 bg-slate-50/70 p-3'>
        <UploadMedia
            question={newQuestion}
            addFileViaInput={addFileViaInput}
            clearMediaFile={clearMediaFile}
        />
      </div>

      <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
        <div className='space-y-3'>
          <TextInput
              label={t(AQM.question)}
              name='question'
              value={newQuestion.question}
              type='text'
              onChange={handleChange}
          />
          <TextArea
              label={t(AQM.answer)}
              name='answer'
              value={newQuestion.answer}
              onChange={handleChange}
          />

          <CustomSelect
              label={t(AQM.skill)}
              name='skillId'
              value={
                skillList?.filter(
                  (option) => option.value === newQuestion.skillId,
                )[0]
              }
              options={skillList}
              onChange={handleSelectChange}
              creatable={true}
              onCreateOption={onAddNewSkill}
          />
          { Boolean(newQuestion.skillId) && (
            <CustomSelect
                label={t(AQM.subSkill1)}
                name='subSkill1Id'
                value={
                  subSkills1?.filter(
                    (option) => option.value === newQuestion.subSkill1Id,
                  )[0]
                }
                options={subSkills1}
                onChange={handleSelectChange}
                creatable={true}
                clearable={true}
                onCreateOption={(input) =>
                  onAddNewSkill(input, newQuestion.skillId, '1')
                }
            />
          ) }
          { newQuestion.subSkill1Id && (
            <CustomSelect
                label={t(AQM.subSkill2)}
                name='subSkill2Id'
                value={
                  subSkills2?.filter(
                    (option) => option.value === newQuestion.subSkill2Id,
                  )[0]
                }
                options={subSkills2}
                onChange={handleSelectChange}
                creatable={true}
                clearable={true}
                onCreateOption={(input) =>
                  onAddNewSkill(input, newQuestion.subSkill1Id, '2')
                }
            />
          ) }
          { newQuestion.subSkill2Id && (
            <CustomSelect
                label={t(AQM.subSkill3)}
                name='subSkill3Id'
                value={
                  subSkills3?.filter(
                    (option) => option.value === newQuestion.subSkill3Id,
                  )[0]
                }
                options={subSkills3}
                onChange={handleSelectChange}
                creatable={true}
                clearable={true}
                onCreateOption={(input) =>
                  onAddNewSkill(input, newQuestion.subSkill2Id, '3')
                }
            />
          ) }
        </div>

        <div className='space-y-3'>
          <CustomSelect
              label={t(AQM.level)}
              name='levelId'
              value={
                levelList?.filter(
                  (option) => option.value === newQuestion.levelId,
                )[0]
              }
              options={levelList}
              onChange={handleSelectChange}
          />
          <CustomSelect
              label={t(AQM.jobTitle)}
              name='jobTitle'
              value={selectedJobTitles}
              options={jobTitleList}
              onChange={handleMultiSelectChange}
              isMulti={true}
              clearable
              placeholder={t(AQM.selectJobTitles)}
          />
          <CustomSelect
              label={t(AQM.questionType)}
              name='questionType'
              value={
                newQuestion.questionType
                  ? questionTypes?.filter(
                    (option) => option.value === newQuestion.questionType,
                  )[0]
                  : questionTypes[0]
              }
              options={questionTypes}
              onChange={handleSelectChange}
          />
          <MinuteSecondsInput
              label={t(AQM.timerSeconds)}
              name='time'
              value={newQuestion.time}
              onChange={handleChange}
          />
          <TextInput
              label={t(AQM.pointsForQuestion)}
              name='points'
              value={newQuestion.points.toString()}
              type='number'
              onChange={handleChange}
          />
        </div>
      </div>
      <div className='flex justify-end border-t border-slate-100 pt-3'>
        <Button
            variant='primary'
            label={t(AQM.addQuestion)}
            onClick={() => addNewQuestion(newQuestion)}
            className='min-h-9 w-full px-3 py-1.5 text-sm sm:w-auto'
        />
      </div>
    </div>
  );
};

